import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import type { NoteCard } from "@/lib/note-cards";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

function Card({ card, index }: { card: NoteCard; index: number }) {
  const inner = (
    <div className="relative rounded-xl bg-[oklch(0.27_0.035_150)] p-[6px] shadow-[0_24px_60px_-30px_oklch(0_0_0/0.85)] transition-transform duration-500 group-hover:-translate-y-1.5">
      <div className="rounded-lg border border-gold/40 p-[3px]">
        <div className="parchment overflow-hidden rounded-md px-4 pb-5 pt-6 text-center">
          <h3 className="font-display text-2xl uppercase tracking-[0.08em] text-[oklch(0.27_0.04_150)] sm:text-[1.65rem]">
            {card.name}
          </h3>
          {card.subtitle && (
            <p className="mt-1 font-display text-[0.62rem] uppercase tracking-[0.22em] text-[oklch(0.32_0.03_60)]">
              {card.subtitle}
            </p>
          )}

          <div className="relative mt-4 overflow-hidden rounded-sm border-2 border-[oklch(0.27_0.035_150)]">
            <div className="aspect-[4/5] overflow-hidden bg-ink">
              {card.imageUrl ? (
                <ResponsiveImage
                  src={card.imageUrl}
                  alt={card.imageAlt}
                  width={768}
                  height={896}
                  sizes={SIZES.portrait}
                  pictureClassName="contents"
                  className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-4xl uppercase tracking-[0.2em] text-[oklch(0.32_0.03_60)/0.4]">
                  {card.name.charAt(0)}
                </div>
              )}
            </div>
          </div>

          {card.blurb && (
            <p className="mx-auto mt-4 max-w-[18rem] font-serif text-[0.95rem] italic leading-snug text-[oklch(0.3_0.03_55)]">
              {card.blurb}
            </p>
          )}

          <p className="mt-4 font-display text-[0.55rem] uppercase tracking-[0.28em] text-[oklch(0.42_0.05_70)]">
            House of Apocrypha
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      {card.characterSlug ? (
        <Link
          to="/character-archive/$slug"
          params={{ slug: card.characterSlug }}
          className="group block"
          aria-label={`${card.name}${card.subtitle ? ` — ${card.subtitle}` : ""}`}
        >
          {inner}
        </Link>
      ) : (
        <div className="group block">{inner}</div>
      )}
    </motion.div>
  );
}

export function FragranceNotes({ cards }: { cards: NoteCard[] }) {
  if (!cards.length) return null;

  return (
    <section className="relative py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <p className="eyebrow">Character Archive</p>
          <h2 className="mt-5 text-3xl text-parchment sm:text-4xl">Key Fragrance Notes</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg italic text-muted-foreground">
            Each note is a character with a history. Draw a card to read its full definition.
          </p>
          <div className="ornament mt-8">
            <span>✶</span>
          </div>
        </div>

        <div className="mt-16 px-2 sm:px-10 lg:px-14">
          <Carousel
            opts={{ align: "start", loop: false, dragFree: true }}
            className="w-full"
          >
            <CarouselContent className="-ml-6">
              {cards.map((c, i) => (
                <CarouselItem
                  key={c.handle}
                  className="basis-[80%] pl-6 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                >
                  <Card card={c} index={i} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden border-gold/40 bg-background/70 text-parchment hover:bg-gold hover:text-primary-foreground sm:flex" />
            <CarouselNext className="hidden border-gold/40 bg-background/70 text-parchment hover:bg-gold hover:text-primary-foreground sm:flex" />
          </Carousel>
        </div>
      </div>
    </section>
  );
}
